import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { Form, Input, InputNumber, Select, DatePicker, TimePicker, Upload, Button, Checkbox, Radio, Spin, Result, Row, Col, Tag, message } from "antd";
import { ArrowLeftOutlined, UploadOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import moment from "moment";
import Service from "../../service";
import currenciesData from "../../constants/currencies";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import usePublicBranding from "./usePublicBranding";
import PublicCareerLayout from "./PublicCareerLayout";
import JobMeta from "./JobMeta";
import ResumeUploadField from "./ResumeUploadField";
import CaptchaField from "./CaptchaField";

const GENDERS = ["Male", "Female", "Other"];
const DOCUMENT_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
const MAX_DOCUMENTS = 5;

const MAX_SKILLS = 50;

// Built-in fields that are required on the original form; used until the
// company's saved form configuration has loaded (or if it fails to load).
const DEFAULT_REQUIRED = { mobile: true, gender: true };
const hiddenStyle = (hidden) => (hidden ? { display: "none" } : undefined);

/** Rules for a company-defined custom field. */
const customFieldRules = (field) => {
  const rules = [];
  if (field.required) {
    rules.push(
      field.fieldType === "checkbox"
        ? { validator: (_, checked) => (checked ? Promise.resolve() : Promise.reject(new Error(`Please accept ${field.fieldLabel}`))) }
        : { required: true, message: `Please fill in ${field.fieldLabel}` }
    );
  }
  if (field.fieldType === "email") rules.push({ type: "email", message: "Please enter a valid email" });
  if (field.fieldType === "phone") rules.push({ pattern: /^\+?[\d\s-]{6,20}$/, message: "Please enter a valid phone number" });
  if (field.fieldType === "url") rules.push({ type: "url", message: "Please enter a valid link starting with http(s)://" });
  if (field.fieldType === "text" || field.fieldType === "textarea") rules.push({ max: field.fieldType === "textarea" ? 2000 : 1000 });
  return rules;
};

/** Input for a company-defined custom field, chosen by its type. */
const CustomFieldInput = ({ field, dateFormat }) => {
  switch (field.fieldType) {
    case "textarea":
      return <Input.TextArea rows={3} maxLength={2000} placeholder={field.placeholder} />;
    case "number":
      return <InputNumber className="career-full-width" placeholder={field.placeholder} />;
    case "date":
      return <DatePicker className="career-full-width" format={dateFormat} placeholder={field.placeholder || "Select date"} />;
    case "time":
      return <TimePicker className="career-full-width" format="hh:mm A" use12Hours placeholder={field.placeholder || "Select time"} />;
    case "datetime":
      return <DatePicker className="career-full-width" showTime format={`${dateFormat} hh:mm A`} placeholder={field.placeholder || "Select date and time"} />;
    case "phone":
      return <Input type="tel" maxLength={20} placeholder={field.placeholder} />;
    case "select":
      return <Select placeholder={field.placeholder || "Select"} options={field.options.map((option) => ({ value: option, label: option }))} />;
    case "multiselect":
      return <Select mode="multiple" placeholder={field.placeholder || "Select"} options={field.options.map((option) => ({ value: option, label: option }))} />;
    case "radio":
      return (
        <Radio.Group>
          {field.options.map((option) => (
            <Radio key={option} value={option}>
              {option}
            </Radio>
          ))}
        </Radio.Group>
      );
    case "checkbox":
      return <Checkbox>{field.placeholder || field.fieldLabel}</Checkbox>;
    default:
      return <Input maxLength={1000} placeholder={field.placeholder} />;
  }
};

/** Plain text input; pressing Enter (or comma) turns the typed skill into a removable tag. */
const SkillsInput = ({ value = [], onChange }) => {
  const [draft, setDraft] = useState("");

  const addSkills = (text) => {
    const additions = String(text || "")
      .split(",")
      .map((skill) => skill.trim().slice(0, 50))
      .filter(Boolean);
    if (!additions.length) return;
    const known = new Set(value.map((skill) => skill.toLowerCase()));
    const fresh = additions.filter((skill, index) => {
      const key = skill.toLowerCase();
      if (known.has(key) || additions.findIndex((other) => other.toLowerCase() === key) !== index) return false;
      known.add(key);
      return true;
    });
    if (fresh.length) onChange([...value, ...fresh].slice(0, MAX_SKILLS));
    else message.info("That skill is already added.");
    setDraft("");
  };

  return (
    <div className="career-skills">
      <Input
        value={draft}
        placeholder="Type a skill and press Enter"
        maxLength={50}
        disabled={value.length >= MAX_SKILLS}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            addSkills(draft);
          }
        }}
        onBlur={() => addSkills(draft)}
      />
      {value.length ? (
        <div className="career-skill-tags">
          {value.map((skill) => (
            <Tag key={skill} closable onClose={() => onChange(value.filter((item) => item !== skill))}>
              {skill}
            </Tag>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const isBlank = (value) => value === undefined || value === null || value === "" || (Array.isArray(value) && !value.length);

// Indian lakh/crore grouping for INR, thousands grouping for everything else.
const groupDigits = (digits, currency) => {
  if (currency === "INR") {
    if (digits.length <= 3) return digits;
    return `${digits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ",")},${digits.slice(-3)}`;
  }
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const makeAmountFormatter = (currency) => (value) => {
  if (value === undefined || value === null || value === "") return "";
  const [integer = "", decimals] = String(value).split(".");
  const grouped = groupDigits(integer.replace(/\D/g, ""), currency);
  return decimals !== undefined ? `${grouped}.${decimals}` : grouped;
};

const parseAmount = (text) => String(text || "").replace(/[^\d.]/g, "");

// Module-level so the Select's options keep a stable identity across renders.
const currencyOptions = currenciesData.map((currency) => ({
  value: currency.code,
  symbol: currency.symbol,
  label: `${currency.symbol}  ${currency.code}  ${currency.name}`,
}));

/**
 * Public, unauthenticated application form for a job opened from its
 * shareable link. On submit the candidate lands in Talent Pool and ATS
 * Tracking automatically.
 */
const CareerPageApply = () => {
  const { companyslug, publicSlug } = useParams();
  const history = useHistory();
  const { branding } = usePublicBranding(companyslug);
  const [form] = Form.useForm();

  const [job, setJob] = useState(null);
  // Company's saved application-form configuration (Form Builder -> ATS Application Form).
  const [formConfig, setFormConfig] = useState({ builtIn: {}, visibleBuiltInSections: null, customSections: [], customFields: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resume, setResume] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [phone, setPhone] = useState({ value: "", dialCode: "" });
  const [currentSalaryCurrency, setCurrentSalaryCurrency] = useState("");
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [currencySearch, setCurrencySearch] = useState("");
  const closeCurrencyList = () => {
    setCurrencyOpen(false);
    setCurrencySearch("");
  };
  const [captcha, setCaptcha] = useState({ token: "", text: "" });
  const [captchaRefresh, setCaptchaRefresh] = useState(0);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const companyCurrency = branding.currency || "INR";
  // Current salary may be in the candidate's own currency; expected salary is always the company's.
  const selectedCurrentCurrency = currentSalaryCurrency || companyCurrency;
  const maxSizeMb = branding.fileUploadSize || 1;

  const isShown = (key) => formConfig.builtIn[key]?.active !== false;
  const isRequired = (key) => (formConfig.builtIn[key] ? formConfig.builtIn[key].required === true : DEFAULT_REQUIRED[key] === true);
  const showSection = (key) => !formConfig.visibleBuiltInSections || formConfig.visibleBuiltInSections.includes(key);
  const customFieldsIn = (sectionKey) => formConfig.customFields.filter((field) => field.sectionKey === sectionKey);

  const renderCustomFields = (sectionKey) =>
    customFieldsIn(sectionKey).map((field) => (
      <Col xs={24} md={field.fieldType === "textarea" ? 24 : 12} lg={field.fieldType === "textarea" ? 24 : 8} key={field.fieldName}>
        <Form.Item
          label={field.fieldType === "checkbox" ? undefined : field.fieldLabel}
          name={field.fieldName}
          valuePropName={field.fieldType === "checkbox" ? "checked" : "value"}
          rules={customFieldRules(field)}
        >
          <CustomFieldInput field={field} dateFormat={branding.dateFormat} />
        </Form.Item>
      </Col>
    ));

  const screeningQuestions = job?.screening_questions || [];
  const screeningFieldName = (question) => `screening_${question.questionId}`;
  const buildScreeningAnswers = (values) =>
    screeningQuestions.reduce((answers, question) => {
      const value = values[screeningFieldName(question)];
      if (value !== undefined && value !== null && String(value).trim() !== "") answers[question.questionId] = String(value).trim();
      return answers;
    }, {});

  const buildCustomAnswers = (values) =>
    formConfig.customFields.reduce((answers, field) => {
      const value = values[field.fieldName];
      if (value === undefined || value === null || value === "") return answers;
      const formatByType = { date: "YYYY-MM-DD", time: "HH:mm", datetime: "YYYY-MM-DD HH:mm" };
      answers[field.fieldName] = formatByType[field.fieldType] ? moment(value).format(formatByType[field.fieldType]) : value;
      return answers;
    }, {});

  const fetchJob = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.publicJobDetail(companyslug, publicSlug),
      });
      if (response?.data?.status === 200) {
        setJob(response.data.data);
      } else {
        setError(response?.data?.message || "This job could not be found.");
      }
    } catch (err) {
      setError("Unable to load this job right now. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [companyslug, publicSlug]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  useEffect(() => {
    let active = true;
    Service.makeAPICall({ methodName: Service.getMethod, api_url: Service.publicApplicationForm(companyslug) })
      .then((response) => {
        if (active && response?.data?.status === 200 && response.data.data) {
          const { builtIn, visibleBuiltInSections, customSections, customFields } = response.data.data;
          setFormConfig({ builtIn: builtIn || {}, visibleBuiltInSections: visibleBuiltInSections || null, customSections: customSections || [], customFields: customFields || [] });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [companyslug]);

  const beforeDocumentUpload = (file) => {
    const extension = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
    if (!DOCUMENT_ACCEPT.split(",").includes(extension)) {
      message.error("Documents must be PDF, DOC, DOCX, JPG or PNG files.");
      return Upload.LIST_IGNORE;
    }
    if (file.size > maxSizeMb * 1024 * 1024) {
      message.error(`Each document must be smaller than ${maxSizeMb} MB.`);
      return Upload.LIST_IGNORE;
    }
    if (documents.length >= MAX_DOCUMENTS) {
      message.error(`You can attach up to ${MAX_DOCUMENTS} documents.`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleSubmit = async (values) => {
    if (!resume?.resumePath) {
      message.error("Please upload your resume.");
      return;
    }
    const nationalNumber = phone.value.startsWith(phone.dialCode) ? phone.value.slice(phone.dialCode.length) : phone.value;
    const hasPhone = Boolean(nationalNumber);
    if (isShown("mobile") && (isRequired("mobile") || hasPhone) && (!phone.dialCode || nationalNumber.length < 6)) {
      message.error("Please enter a valid mobile number.");
      return;
    }
    if (isShown("documents") && isRequired("documents") && !documents.length) {
      message.error("Please attach at least one document.");
      return;
    }

    const formData = new FormData();
    formData.append("captcha_token", captcha.token || "");
    formData.append("captcha_answer", captcha.text || "");
    formData.append("website", values.website || "");
    formData.append("first_name", values.first_name || "");
    formData.append("middle_name", values.middle_name || "");
    formData.append("last_name", values.last_name || "");
    formData.append("email", values.email || "");
    if (isShown("mobile") && hasPhone) {
      formData.append("mobile_country_code", `+${phone.dialCode}`);
      formData.append("mobile", nationalNumber);
    }
    formData.append("gender", values.gender || "");
    formData.append("current_salary_currency", selectedCurrentCurrency);
    ["experience_years", "experience_months", "current_salary", "expected_salary", "available_to_join_days"].forEach((key) => {
      if (!isBlank(values[key])) formData.append(key, String(values[key]));
    });
    ["preferred_location", "current_location", "current_company", "current_designation"].forEach((key) => {
      if (!isBlank(values[key])) formData.append(key, values[key]);
    });
    formData.append("skills", JSON.stringify(values.skills || []));
    if (values.last_working_day) formData.append("last_working_day", moment(values.last_working_day).format("YYYY-MM-DD"));
    formData.append("custom_answers", JSON.stringify(buildCustomAnswers(values)));
    formData.append("screening_answers", JSON.stringify(buildScreeningAnswers(values)));
    formData.append("consent", consent ? "true" : "false");
    formData.append("resume_path", resume.resumePath);
    formData.append("resume_filename", resume.filename || "");
    documents.forEach((file) => formData.append("documents", file.originFileObj || file));

    setSubmitting(true);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.publicApplyJob(companyslug, publicSlug),
        body: formData,
      });
      if (response?.data?.status === 200) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        message.error(response?.data?.message || "We could not submit your application. Please try again.");
        setCaptcha({ token: "", text: "" });
        setCaptchaRefresh((count) => count + 1);
      }
    } catch (err) {
      message.error("We could not submit your application. Please try again.");
      setCaptchaRefresh((count) => count + 1);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicCareerLayout companyslug={companyslug} branding={branding}>
        <div className="career-state">
          <Spin size="large" />
        </div>
      </PublicCareerLayout>
    );
  }

  if (error || !job) {
    return (
      <PublicCareerLayout companyslug={companyslug} branding={branding} title="Job not found">
        <div className="career-state">
          <NodataFoundIcon />
          <p>{error || "This job could not be found."}</p>
          <Link to={`/${companyslug}/careers`}>View all openings</Link>
        </div>
      </PublicCareerLayout>
    );
  }

  if (submitted) {
    return (
      <PublicCareerLayout
        companyslug={companyslug}
        branding={branding}
        title="Application submitted"
        hero={
          <>
            <h1 className="career-hero-title">{job.title}</h1>
            <JobMeta job={job} />
          </>
        }
      >
        <div className="career-card career-success">
          <Result
            status="success"
            title="Application submitted"
            subTitle={`Thank you for applying for ${job.title}. We have emailed you a confirmation and our team will be in touch if there is a match.`}
          />
        </div>
      </PublicCareerLayout>
    );
  }

  const canSubmit = Boolean(resume?.resumePath) && consent && Boolean(captcha.text);

  return (
    <PublicCareerLayout
      companyslug={companyslug}
      branding={branding}
      title={`Apply - ${job.title}`}
      hero={
        <>
          <h1 className="career-hero-title">{job.title}</h1>
          <JobMeta job={job} />
        </>
      }
    >
      <Link to={`/${companyslug}/careers/${publicSlug}`} className="career-back-link">
        <ArrowLeftOutlined /> Back to job details
      </Link>

      <div className="career-card career-narrow career-apply-form">
        <div className="career-apply-heading">
          <h2 className="career-apply-title">Apply for this job</h2>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit} scrollToFirstError>
          <section className="career-form-section" style={hiddenStyle(!showSection("personal"))}>
            <h2 className="career-section-title">Personal details</h2>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12} lg={8}>
                <Form.Item label="First Name" name="first_name" rules={[{ required: true, message: "Please enter your first name" }, { max: 60 }]}>
                  <Input placeholder="Enter first name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("middle_name"))}>
                <Form.Item label="Middle Name" name="middle_name" rules={[{ required: isRequired("middle_name"), message: "Please enter your middle name" }, { max: 60 }]}>
                  <Input placeholder="Enter middle name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item label="Last Name" name="last_name" rules={[{ required: true, message: "Please enter your last name" }, { max: 60 }]}>
                  <Input placeholder="Enter last name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("mobile"))}>
                <Form.Item label="Mobile Phone" required={isRequired("mobile")}>
                  <PhoneInput
                    country="in"
                    value={phone.value}
                    onChange={(value, data) => setPhone({ value, dialCode: data?.dialCode || "" })}
                    countryCodeEditable={false}
                    inputProps={{ name: "mobile", required: true }}
                    containerClass="career-phone"
                    placeholder="Enter mobile number"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[{ required: true, message: "Please enter your email" }, { type: "email", message: "Please enter a valid email" }]}
                >
                  <Input placeholder="Enter email" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("gender"))}>
                <Form.Item label="Gender" name="gender" rules={[{ required: isRequired("gender"), message: "Please select your gender" }]}>
                  <Select placeholder="Select">
                    {GENDERS.map((gender) => (
                      <Select.Option key={gender} value={gender}>
                        {gender}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            {renderCustomFields("personal")}
            </Row>
          </section>

          <section className="career-form-section" style={hiddenStyle(!showSection("experience"))}>
            <h2 className="career-section-title">Experience and compensation</h2>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("experience"))}>
                <Form.Item label="Experience" required={isRequired("experience")}>
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name="experience_years" noStyle rules={[{ required: isRequired("experience"), type: "number", message: "Please enter your experience" }, { type: "number", min: 0, max: 50 }]}>
                        <InputNumber className="career-full-width" placeholder="0" min={0} max={50} precision={0} addonAfter="Years" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="experience_months" noStyle rules={[{ type: "number", min: 0, max: 11 }]}>
                        <InputNumber className="career-full-width" placeholder="0" min={0} max={11} precision={0} addonAfter="Months" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("current_company"))}>
                <Form.Item label="Current Company" name="current_company" rules={[{ required: isRequired("current_company"), message: "Please enter your current company" }, { max: 120 }]}>
                  <Input placeholder="Enter current company" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("current_designation"))}>
                <Form.Item label="Current Designation" name="current_designation" rules={[{ required: isRequired("current_designation"), message: "Please enter your current designation" }, { max: 120 }]}>
                  <Input placeholder="Enter current designation" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("current_salary"))}>
                <Form.Item label="Current Annual Salary" required={isRequired("current_salary")}>
                  <Input.Group compact className="career-salary-group">
                    <Select
                      className="career-currency-select"
                      showSearch
                      value={selectedCurrentCurrency}
                      open={currencyOpen}
                      searchValue={currencySearch}
                      onSearch={setCurrencySearch}
                      onFocus={() => setCurrencyOpen(true)}
                      onBlur={closeCurrencyList}
                      onDropdownVisibleChange={(next) => {
                        // Stay open while the user is typing; it closes on select, Enter, Escape or blur.
                        if (next || !currencySearch) setCurrencyOpen(next);
                      }}
                      onSelect={(code) => {
                        setCurrentSalaryCurrency(code);
                        closeCurrencyList();
                      }}
                      onInputKeyDown={(event) => {
                        if (event.key === "Escape") closeCurrencyList();
                      }}
                      optionLabelProp="symbol"
                      optionFilterProp="label"
                      dropdownMatchSelectWidth={300}
                      options={currencyOptions}
                    />
                    <Form.Item name="current_salary" noStyle rules={[{ required: isRequired("current_salary"), type: "number", message: "Please enter your current salary" }, { type: "number", min: 0 }]}>
                      <InputNumber
                        className="career-salary-input"
                        min={0}
                        formatter={makeAmountFormatter(selectedCurrentCurrency)}
                        parser={parseAmount}
                        placeholder="Enter current salary"
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("expected_salary"))}>
                <Form.Item label={`Expected Annual Salary (${companyCurrency})`} required={isRequired("expected_salary")}>
                  <Input.Group compact className="career-salary-group">
                    <Select className="career-currency-select" disabled value={companyCurrency} optionLabelProp="symbol" options={currencyOptions} />
                    <Form.Item name="expected_salary" noStyle rules={[{ required: isRequired("expected_salary"), type: "number", message: "Please enter your expected salary" }, { type: "number", min: 0 }]}>
                      <InputNumber
                        className="career-salary-input"
                        min={0}
                        formatter={makeAmountFormatter(companyCurrency)}
                        parser={parseAmount}
                        placeholder="Enter expected salary"
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
            {renderCustomFields("experience")}
            </Row>
          </section>

          <section className="career-form-section" style={hiddenStyle(!showSection("location"))}>
            <h2 className="career-section-title">Location and availability</h2>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("current_location"))}>
                <Form.Item label="Current Location" name="current_location" rules={[{ required: isRequired("current_location"), message: "Please enter your current location" }, { max: 120 }]}>
                  <Input placeholder="Enter current location" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("preferred_location"))}>
                <Form.Item label="Preferred Location" name="preferred_location" rules={[{ required: isRequired("preferred_location"), message: "Please enter your preferred location" }, { max: 120 }]}>
                  <Input placeholder="Enter preferred location" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("available_to_join_days"))}>
                <Form.Item label="Available To Join (in days)" name="available_to_join_days" rules={[{ required: isRequired("available_to_join_days"), type: "number", message: "Please enter the days you need to join" }, { type: "number", min: 0, max: 365 }]}>
                  <InputNumber className="career-full-width" min={0} max={365} precision={0} placeholder="e.g. 30" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8} style={hiddenStyle(!isShown("last_working_day"))}>
                <Form.Item label="Last Working Day" name="last_working_day" rules={[{ required: isRequired("last_working_day"), message: "Please select your last working day" }]}>
                  <DatePicker className="career-full-width" format={branding.dateFormat} placeholder="Select date" />
                </Form.Item>
              </Col>
            {renderCustomFields("location")}
            </Row>
          </section>

          <section className="career-form-section" style={hiddenStyle(!showSection("skills"))}>
            <h2 className="career-section-title">Skills</h2>
            <Form.Item name="skills" style={hiddenStyle(!isShown("skills"))} rules={[{ required: isRequired("skills"), type: "array", message: "Please add at least one skill" }, { type: "array", max: 50 }]}>
              <SkillsInput />
            </Form.Item>
          <Row gutter={[16, 0]}>{renderCustomFields("skills")}</Row>
          </section>

          <section className="career-form-section" style={hiddenStyle(!showSection("resume"))}>
            <h2 className="career-section-title">Resume</h2>
            <ResumeUploadField companyslug={companyslug} publicSlug={publicSlug} maxSizeMb={maxSizeMb} value={resume} onChange={setResume} />
          <Row gutter={[16, 0]}>{renderCustomFields("resume")}</Row>
          </section>

          <section className="career-form-section" style={hiddenStyle(!showSection("documents"))}>
            <h2 className="career-section-title">Additional documents</h2>
            <div style={hiddenStyle(!isShown("documents"))}>
            <Upload
              multiple
              accept={DOCUMENT_ACCEPT}
              beforeUpload={beforeDocumentUpload}
              fileList={documents}
              onChange={({ fileList }) => setDocuments(fileList.slice(0, MAX_DOCUMENTS))}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
            <div className="career-field-hint">
              {isRequired("documents") ? "Required" : "Optional"}. Up to {MAX_DOCUMENTS} files (PDF, DOC, DOCX, JPG, PNG), {maxSizeMb} MB each.
            </div>
            </div>
            <Row gutter={[16, 0]}>{renderCustomFields("documents")}</Row>
          </section>

          {formConfig.customSections.map((section) =>
            customFieldsIn(section.key).length ? (
              <section className="career-form-section" key={section.key}>
                <h2 className="career-section-title">{section.name}</h2>
                <Row gutter={[16, 0]}>{renderCustomFields(section.key)}</Row>
              </section>
            ) : null
          )}

          {screeningQuestions.length > 0 && (
            <section className="career-form-section">
              <h2 className="career-section-title">Pre-Screening Questions</h2>
              <Row gutter={[16, 0]}>
                {screeningQuestions.map((question) => (
                  <Col xs={24} md={question.answerType === "text" ? 24 : 12} key={question.questionId}>
                    <Form.Item
                      label={question.question}
                      name={screeningFieldName(question)}
                      rules={question.required ? [{ required: true, whitespace: true, message: "Please answer this question" }] : []}
                    >
                      {question.answerType === "text" ? (
                        <Input.TextArea rows={2} maxLength={1000} />
                      ) : (
                        <Radio.Group>
                          {(question.answerType === "yes_no" ? ["Yes", "No"] : question.options || []).map((option) => (
                            <Radio key={option} value={option}>
                              {option}
                            </Radio>
                          ))}
                        </Radio.Group>
                      )}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </section>
          )}

          <section className="career-form-section">
            <Form.Item name="website" className="career-honeypot" aria-hidden="true">
              <Input tabIndex={-1} autoComplete="off" />
            </Form.Item>
            <Checkbox checked={consent} onChange={(event) => setConsent(event.target.checked)} className="career-consent">
              By applying, I accept that the information and documents I provide will be processed as part of this job application and used to contact me about this and similar opportunities.
            </Checkbox>
            <div className="career-captcha-row">
              <CaptchaField companyslug={companyslug} value={captcha} onChange={setCaptcha} refreshKey={captchaRefresh} />
            </div>
            <div className="career-submit-row">
              <Button type="primary" size="large" htmlType="submit" loading={submitting} disabled={!canSubmit} className="career-submit">
                Apply Now
              </Button>
            </div>
          </section>
        </Form>
      </div>
    </PublicCareerLayout>
  );
};

export default CareerPageApply;
